import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { JSX, ReactNode } from 'react';

/**
 * Shared hover-tooltip layer for chart marks.
 *
 * Every chart mark carries `data-tip` holding plain text lines separated by
 * `\n`; the first line is rendered as the bold heading. Text rather than HTML,
 * because the tip content includes partner and brand names and injecting those
 * as markup is an XSS route the prototype left open via `innerHTML`.
 *
 * Mounted once at the app root. A single delegated listener is cheaper than a
 * handler per mark, and charts here can render several hundred marks.
 */

interface TipState {
  readonly text: string;
  readonly x: number;
  readonly y: number;
}

const OFFSET = 12;

export function TooltipLayer(): JSX.Element | null {
  const [tip, setTip] = useState<TipState | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onMove = (event: MouseEvent): void => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const host = target.closest('[data-tip]');
      const text = host?.getAttribute('data-tip');
      if (!text) {
        setTip(null);
        return;
      }
      setTip({ text, x: event.clientX, y: event.clientY });
    };
    const hide = (): void => {
      setTip(null);
    };

    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('mouseleave', hide, true);
    window.addEventListener('scroll', hide, true);
    return () => {
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('mouseleave', hide, true);
      window.removeEventListener('scroll', hide, true);
    };
  }, []);

  /**
   * Flip to the other side of the cursor rather than overflow the viewport.
   *
   * Measured in a layout effect, not during render: the box has to exist before
   * it can be measured, and reading a ref during render is both unreliable and
   * a lint error. The first paint places the box naively at the cursor and this
   * corrects it before the browser shows the frame, so there is no visible jump.
   */
  useLayoutEffect(() => {
    if (tip === null) return;
    const box = boxRef.current;
    if (box === null) return;

    const rect = box.getBoundingClientRect();
    let left = tip.x + OFFSET;
    let top = tip.y + OFFSET;
    if (left + rect.width > window.innerWidth - 8) left = tip.x - rect.width - OFFSET;
    if (top + rect.height > window.innerHeight - 8) top = tip.y - rect.height - OFFSET;

    box.style.left = `${left}px`;
    box.style.top = `${top}px`;
  }, [tip]);

  if (tip === null) return null;

  const [heading, ...rest] = tip.text.split('\n');
  return (
    <div
      ref={boxRef}
      className="tt-box"
      style={{ left: tip.x + OFFSET, top: tip.y + OFFSET }}
      role="tooltip"
    >
      <b>{heading}</b>
      {rest.map((line, i) => (
        <div key={i}>{line}</div>
      ))}
    </div>
  );
}

/**
 * Builds a `data-tip` value. First line is the heading.
 * Falsy lines are dropped so callers can conditionally include detail.
 */
export const tip = (heading: string, ...lines: (string | false | null | undefined)[]): string =>
  [heading, ...lines.filter((l): l is string => typeof l === 'string' && l.length > 0)].join('\n');

/** Spread onto any element to give it a tooltip. */
export const tipAttrs = (text: string): { readonly 'data-tip': string } => ({ 'data-tip': text });

/** Wraps children in a span carrying a tooltip. */
export function WithTip({
  text,
  children,
  className,
}: {
  readonly text: string;
  readonly children: ReactNode;
  readonly className?: string | undefined;
}): JSX.Element {
  return (
    <span data-tip={text} className={className}>
      {children}
    </span>
  );
}
